/**
 * Cache Performance Monitoring Service
 * 
 * Tracks and analyzes Redis cache performance metrics:
 * - Hit rates and miss rates
 * - Operation latencies
 * - Memory usage
 * - Error rates
 * - Per-feature statistics
 * 
 * Features:
 * - Real-time metrics tracking
 * - Automatic alerting on performance degradation
 * - Performance trend analysis
 * - Dashboard-ready statistics
 * 
 * Usage:
 * ```javascript
 * import cacheMonitor from './cacheMonitoringService';
 * 
 * // Track cache operation
 * cacheMonitor.trackHit('job_search');
 * cacheMonitor.trackMiss('user_profile');
 * cacheMonitor.trackLatency('static_data', 5);
 * 
 * // Get statistics
 * const stats = await cacheMonitor.getStats();
 * 
 * // Get feature-specific stats
 * const jobStats = await cacheMonitor.getFeatureStats('job_search');
 * 
 * // Enable alerting
 * cacheMonitor.startAlerting({
 *   minHitRate: 50,
 *   maxLatency: 100,
 *   onAlert: (alert) => console.error('Cache Alert:', alert)
 * });
 * ```
 */

import redisClient from '../utils/redisClient.js';

const isDev = import.meta.env?.MODE === 'development' || import.meta.env?.DEV;

// Monitoring key prefixes
const METRICS_PREFIX = 'cache_metrics';
const FEATURE_PREFIX = 'cache_feature';

class CacheMonitoringService {
  constructor() {
    this.alertConfig = {
      enabled: false,
      minHitRate: 50, // Alert if hit rate drops below 50%
      maxLatency: 100, // Alert if latency exceeds 100ms
      checkInterval: 60000, // Check every minute
      onAlert: null
    };
    this.alertInterval = null;
    this.localMetrics = {
      hits: 0,
      misses: 0,
      errors: 0,
      latencies: [],
      features: {}
    };
  }

  /**
   * Track cache hit
   * @param {string} feature - Feature name (e.g., 'job_search', 'user_profile')
   */
  async trackHit(feature = 'unknown') {
    this.localMetrics.hits++;
    
    if (redisClient.isAvailable()) {
      try {
        await Promise.all([
          redisClient.incr(`${METRICS_PREFIX}:hits`),
          redisClient.incr(`${FEATURE_PREFIX}:${feature}:hits`)
        ]);
      } catch (error) {
        console.error('[CacheMonitor] Failed to track hit:', error);
      }
    }
    
    // Track feature-specific metrics locally
    if (!this.localMetrics.features[feature]) {
      this.localMetrics.features[feature] = { hits: 0, misses: 0, latencies: [] };
    }
    this.localMetrics.features[feature].hits++;
  }

  /**
   * Track cache miss
   * @param {string} feature - Feature name
   */
  async trackMiss(feature = 'unknown') {
    this.localMetrics.misses++;
    
    if (redisClient.isAvailable()) {
      try {
        await Promise.all([
          redisClient.incr(`${METRICS_PREFIX}:misses`),
          redisClient.incr(`${FEATURE_PREFIX}:${feature}:misses`)
        ]);
      } catch (error) {
        console.error('[CacheMonitor] Failed to track miss:', error);
      }
    }
    
    // Track feature-specific metrics locally
    if (!this.localMetrics.features[feature]) {
      this.localMetrics.features[feature] = { hits: 0, misses: 0, latencies: [] };
    }
    this.localMetrics.features[feature].misses++;
  }

  /**
   * Track cache operation latency
   * @param {string} feature - Feature name
   * @param {number} latencyMs - Latency in milliseconds
   */
  async trackLatency(feature = 'unknown', latencyMs) {
    this.localMetrics.latencies.push(latencyMs);
    
    // Keep only last 100 latencies in memory
    if (this.localMetrics.latencies.length > 100) {
      this.localMetrics.latencies.shift();
    }
    
    // Track feature-specific latencies
    if (!this.localMetrics.features[feature]) {
      this.localMetrics.features[feature] = { hits: 0, misses: 0, latencies: [] };
    }
    this.localMetrics.features[feature].latencies.push(latencyMs);
    
    // Keep only last 50 latencies per feature
    if (this.localMetrics.features[feature].latencies.length > 50) {
      this.localMetrics.features[feature].latencies.shift();
    }
    
    // Store in Redis (with sampling to reduce writes)
    if (redisClient.isAvailable() && Math.random() < 0.1) { // 10% sampling
      try {
        const key = `${METRICS_PREFIX}:latency:${feature}`;
        const timestamp = Date.now();
        await redisClient.set(`${key}:${timestamp}`, latencyMs, 3600); // 1 hour TTL
      } catch (error) {
        console.error('[CacheMonitor] Failed to track latency:', error);
      }
    }
  }

  /**
   * Track cache error
   * @param {string} feature - Feature name
   * @param {Error} error - Error object
   */
  async trackError(feature = 'unknown', error) {
    this.localMetrics.errors++;
    
    if (redisClient.isAvailable()) {
      try {
        await Promise.all([
          redisClient.incr(`${METRICS_PREFIX}:errors`),
          redisClient.incr(`${FEATURE_PREFIX}:${feature}:errors`)
        ]);
      } catch (err) {
        console.error('[CacheMonitor] Failed to track error:', err);
      }
    }
    
    if (isDev) {
      console.error(`[CacheMonitor] Error in ${feature}:`, error);
    }
  }

  /**
   * Get overall cache statistics
   * @returns {Promise<Object>} Cache statistics
   */
  async getStats() {
    const stats = {
      local: this._calculateLocalStats(),
      redis: null,
      combined: null
    };

    // Get Redis metrics if available
    if (redisClient.isAvailable()) {
      try {
        const [hits, misses, errors] = await Promise.all([
          redisClient.get(`${METRICS_PREFIX}:hits`),
          redisClient.get(`${METRICS_PREFIX}:misses`),
          redisClient.get(`${METRICS_PREFIX}:errors`)
        ]);

        const totalHits = parseInt(hits) || 0;
        const totalMisses = parseInt(misses) || 0;
        const totalErrors = parseInt(errors) || 0;
        const totalRequests = totalHits + totalMisses;

        stats.redis = {
          hits: totalHits,
          misses: totalMisses,
          errors: totalErrors,
          totalRequests,
          hitRate: totalRequests > 0 ? ((totalHits / totalRequests) * 100).toFixed(2) : '0.00',
          missRate: totalRequests > 0 ? ((totalMisses / totalRequests) * 100).toFixed(2) : '0.00',
          errorRate: totalRequests > 0 ? ((totalErrors / totalRequests) * 100).toFixed(2) : '0.00'
        };
      } catch (error) {
        console.error('[CacheMonitor] Failed to get Redis stats:', error);
      }
    }

    // Combine local and Redis stats
    if (stats.redis) {
      const combinedHits = stats.local.hits + stats.redis.hits;
      const combinedMisses = stats.local.misses + stats.redis.misses;
      const combinedTotal = combinedHits + combinedMisses;

      stats.combined = {
        hits: combinedHits,
        misses: combinedMisses,
        errors: stats.local.errors + stats.redis.errors,
        totalRequests: combinedTotal,
        hitRate: combinedTotal > 0 ? ((combinedHits / combinedTotal) * 100).toFixed(2) : '0.00',
        missRate: combinedTotal > 0 ? ((combinedMisses / combinedTotal) * 100).toFixed(2) : '0.00',
        avgLatency: stats.local.avgLatency,
        p95Latency: stats.local.p95Latency,
        p99Latency: stats.local.p99Latency
      };
    } else {
      stats.combined = stats.local;
    }

    return stats;
  }

  /**
   * Get feature-specific statistics
   * @param {string} feature - Feature name
   * @returns {Promise<Object>} Feature statistics
   */
  async getFeatureStats(feature) {
    const localFeature = this.localMetrics.features[feature] || { hits: 0, misses: 0, latencies: [] };
    const localStats = {
      hits: localFeature.hits,
      misses: localFeature.misses,
      totalRequests: localFeature.hits + localFeature.misses,
      hitRate: '0.00',
      avgLatency: 0,
      p95Latency: 0
    };

    if (localStats.totalRequests > 0) {
      localStats.hitRate = ((localFeature.hits / localStats.totalRequests) * 100).toFixed(2);
    }

    if (localFeature.latencies.length > 0) {
      const sorted = [...localFeature.latencies].sort((a, b) => a - b);
      const sum = sorted.reduce((a, b) => a + b, 0);
      localStats.avgLatency = (sum / sorted.length).toFixed(2);
      localStats.p95Latency = sorted[Math.floor(sorted.length * 0.95)] || 0;
    }

    // Get Redis metrics if available
    if (redisClient.isAvailable()) {
      try {
        const [hits, misses, errors] = await Promise.all([
          redisClient.get(`${FEATURE_PREFIX}:${feature}:hits`),
          redisClient.get(`${FEATURE_PREFIX}:${feature}:misses`),
          redisClient.get(`${FEATURE_PREFIX}:${feature}:errors`)
        ]);

        const redisHits = parseInt(hits) || 0;
        const redisMisses = parseInt(misses) || 0;
        const redisErrors = parseInt(errors) || 0;
        const redisTotal = redisHits + redisMisses;

        return {
          local: localStats,
          redis: {
            hits: redisHits,
            misses: redisMisses,
            errors: redisErrors,
            totalRequests: redisTotal,
            hitRate: redisTotal > 0 ? ((redisHits / redisTotal) * 100).toFixed(2) : '0.00'
          },
          combined: {
            hits: localStats.hits + redisHits,
            misses: localStats.misses + redisMisses,
            errors: redisErrors,
            totalRequests: localStats.totalRequests + redisTotal,
            hitRate: ((localStats.hits + redisHits) / (localStats.totalRequests + redisTotal) * 100).toFixed(2),
            avgLatency: localStats.avgLatency,
            p95Latency: localStats.p95Latency
          }
        };
      } catch (error) {
        console.error('[CacheMonitor] Failed to get feature stats:', error);
      }
    }

    return { local: localStats, redis: null, combined: localStats };
  }

  /**
   * Get all feature statistics
   * @returns {Promise<Object>} All features' statistics
   */
  async getAllFeatureStats() {
    const features = Object.keys(this.localMetrics.features);
    const stats = {};

    for (const feature of features) {
      stats[feature] = await this.getFeatureStats(feature);
    }

    return stats;
  }

  /**
   * Start automatic alerting
   * @param {Object} config - Alert configuration
   */
  startAlerting(config = {}) {
    this.alertConfig = {
      ...this.alertConfig,
      ...config,
      enabled: true
    };

    if (this.alertInterval) {
      console.log('[CacheMonitor] Alerting already started');
      return;
    }

    // DISABLED: Periodic alert checks fire DB queries contributing to RAM pressure.
    console.log('[CacheMonitor] Alerting disabled for RAM optimization');
    return;
  }

  /**
   * Stop automatic alerting
   */
  stopAlerting() {
    if (this.alertInterval) {
      clearInterval(this.alertInterval);
      this.alertInterval = null;
      this.alertConfig.enabled = false;
      console.log('[CacheMonitor] Alerting stopped');
    }
  }

  /**
   * Check for alert conditions
   * @private
   */
  async _checkAlerts() {
    if (!this.alertConfig.enabled) return;

    try {
      const stats = await this.getStats();
      const alerts = [];

      // Check hit rate
      if (stats.combined) {
        const hitRate = parseFloat(stats.combined.hitRate);
        if (hitRate < this.alertConfig.minHitRate) {
          alerts.push({
            type: 'low_hit_rate',
            severity: 'warning',
            message: `Cache hit rate (${hitRate.toFixed(2)}%) is below threshold (${this.alertConfig.minHitRate}%)`,
            value: hitRate,
            threshold: this.alertConfig.minHitRate,
            timestamp: new Date()
          });
        }

        // Check latency
        const avgLatency = parseFloat(stats.combined.avgLatency);
        if (avgLatency > this.alertConfig.maxLatency) {
          alerts.push({
            type: 'high_latency',
            severity: 'warning',
            message: `Average cache latency (${avgLatency.toFixed(2)}ms) exceeds threshold (${this.alertConfig.maxLatency}ms)`,
            value: avgLatency,
            threshold: this.alertConfig.maxLatency,
            timestamp: new Date()
          });
        }
      }

      // Check Redis connection
      if (!redisClient.isAvailable()) {
        alerts.push({
          type: 'redis_unavailable',
          severity: 'critical',
          message: 'Redis connection is not available',
          timestamp: new Date()
        });
      }

      // Trigger alerts
      if (alerts.length > 0 && this.alertConfig.onAlert) {
        alerts.forEach(alert => this.alertConfig.onAlert(alert));
      }

      if (alerts.length > 0 && isDev) {
        console.warn('[CacheMonitor] Alerts triggered:', alerts);
      }
    } catch (error) {
      console.error('[CacheMonitor] Alert check failed:', error);
    }
  }

  /**
   * Calculate local statistics
   * @private
   */
  _calculateLocalStats() {
    const { hits, misses, errors, latencies } = this.localMetrics;
    const totalRequests = hits + misses;

    const stats = {
      hits,
      misses,
      errors,
      totalRequests,
      hitRate: totalRequests > 0 ? ((hits / totalRequests) * 100).toFixed(2) : '0.00',
      missRate: totalRequests > 0 ? ((misses / totalRequests) * 100).toFixed(2) : '0.00',
      avgLatency: '0.00',
      p95Latency: 0,
      p99Latency: 0
    };

    if (latencies.length > 0) {
      const sorted = [...latencies].sort((a, b) => a - b);
      const sum = sorted.reduce((a, b) => a + b, 0);
      stats.avgLatency = (sum / sorted.length).toFixed(2);
      stats.p95Latency = sorted[Math.floor(sorted.length * 0.95)] || 0;
      stats.p99Latency = sorted[Math.floor(sorted.length * 0.99)] || 0;
    }

    return stats;
  }

  /**
   * Reset all metrics
   */
  async resetMetrics() {
    this.localMetrics = {
      hits: 0,
      misses: 0,
      errors: 0,
      latencies: [],
      features: {}
    };

    if (redisClient.isAvailable()) {
      try {
        // Delete all metric keys
        await Promise.all([
          redisClient.del(`${METRICS_PREFIX}:hits`),
          redisClient.del(`${METRICS_PREFIX}:misses`),
          redisClient.del(`${METRICS_PREFIX}:errors`)
        ]);
      } catch (error) {
        console.error('[CacheMonitor] Failed to reset Redis metrics:', error);
      }
    }

    console.log('[CacheMonitor] Metrics reset');
  }

  /**
   * Get monitoring status
   * @returns {Object} Status information
   */
  getStatus() {
    return {
      alertingEnabled: this.alertConfig.enabled,
      redisAvailable: redisClient.isAvailable(),
      localMetrics: this._calculateLocalStats(),
      alertConfig: {
        minHitRate: this.alertConfig.minHitRate,
        maxLatency: this.alertConfig.maxLatency,
        checkInterval: this.alertConfig.checkInterval
      }
    };
  }
}

// Export singleton instance
export default new CacheMonitoringService();
